(() => {
  if (window.GuideLightProductFormsLoaded) return;
  window.GuideLightProductFormsLoaded = true;

  const SELECTOR = '[data-guidelight-product-form]';

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (number == null || Number.isNaN(number)) return '0';
    const value = (number / 100).toFixed(precision);
    const parts = value.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    const money = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents);
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${formatWithDelimiters(money)}`;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(money, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(money, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(money, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(money, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(money, 2);
    }
    return format.replace(match[0], value);
  };

  class GuideLightProductForm {
    constructor(root) {
      if (!root || root.dataset.guidelightInitialized === 'true') return;
      root.dataset.guidelightInitialized = 'true';
      this.root = root;
      this.form = root.querySelector('form');
      this.variantInput = root.querySelector('[data-guidelight-variant-id]');
      this.optionInputs = [...root.querySelectorAll('[data-guidelight-option]')];
      this.addButton = root.querySelector('[data-guidelight-add]');
      this.addLabel = root.querySelector('[data-guidelight-add-label]');
      this.price = root.querySelector('[data-guidelight-current-price]');
      this.compare = root.querySelector('[data-guidelight-compare-price]');
      this.save = root.querySelector('[data-guidelight-save]');
      this.message = root.querySelector('[data-guidelight-form-message]');
      this.section = root.closest('.gl-section');
      this.productImage = this.section?.querySelector('[data-guidelight-product-image]');
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.addText = root.dataset.addText || 'Add to cart';
      this.soldOutText = root.dataset.soldOutText || 'Sold out';
      this.productHandle = root.dataset.productHandle || '';

      try {
        this.variants = JSON.parse(root.querySelector('[data-guidelight-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
        console.warn('GuideLight: variant data could not be read.', error);
      }

      this.bindEvents();
      this.updateVariant();
    }

    bindEvents() {
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.updateVariant());
      });

      this.root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const input = this.root.querySelector('input[name="quantity"]');
        if (!input) return;
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const input = this.root.querySelector('input[name="quantity"]');
        if (!input) return;
        input.value = Number(input.value || 1) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    selectedOptions() {
      return this.optionInputs
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
    }

    findVariant() {
      if (!this.optionInputs.length) return this.variants[0];
      const selected = this.selectedOptions();
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      );
    }

    updateVariant() {
      const variant = this.findVariant();
      if (!variant) {
        if (this.variantInput) this.variantInput.value = '';
        if (this.addButton) this.addButton.disabled = true;
        if (this.addLabel) this.addLabel.textContent = 'Unavailable';
        if (this.message) this.message.textContent = 'This combination is not available.';
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.addLabel) this.addLabel.textContent = variant.available ? this.addText : this.soldOutText;
      if (this.message) this.message.textContent = variant.available ? '' : 'This selection is currently sold out.';

      if (this.price) this.price.textContent = formatMoney(variant.price, this.moneyFormat);

      const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
      if (this.compare) {
        this.compare.hidden = !hasCompare;
        this.compare.textContent = hasCompare ? formatMoney(variant.compare_at_price, this.moneyFormat) : '';
      }
      if (this.save) {
        this.save.hidden = !hasCompare;
        this.save.textContent = hasCompare
          ? `Save ${formatMoney(variant.compare_at_price - variant.price, this.moneyFormat)}`
          : '';
      }

      const image = variant.featured_image;
      if (image?.src && this.productImage) {
        const source = image.src.startsWith('//') ? `https:${image.src}` : image.src;
        this.productImage.src = source;
        this.productImage.removeAttribute('srcset');
        this.productImage.alt = image.alt || variant.name || variant.title || '';
      }

      document.dispatchEvent(new CustomEvent('guidelight:variant-change', {
        detail: {
          productHandle: this.productHandle,
          variantId: variant.id,
          variantTitle: variant.public_title || variant.title,
          price: variant.price,
          compareAtPrice: variant.compare_at_price,
          available: variant.available,
          moneyFormat: this.moneyFormat,
          formattedPrice: formatMoney(variant.price, this.moneyFormat)
        }
      }));
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => new GuideLightProductForm(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
